import React from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Avatar,
  Chip,
  LinearProgress,
} from '@mui/material';
import {
  Group,
  Feed,
  Assignment,
  TrendingUp,
  Add,
  EmojiEvents,
} from '@mui/icons-material';
import { useAuth, useClubs, usePosts } from '../context/AppContext';
import UserAvatar from '../components/common/UserAvatar';
import RoleBadge from '../components/common/RoleBadge';
import LoadingSpinner from '../components/common/LoadingSpinner';
import ErrorMessage from '../components/common/ErrorMessage';

const DashboardPage: React.FC = () => {
  const { user, isFaculty } = useAuth();
  const { loading: clubsLoading, error: clubsError } = useClubs();
  const { posts } = usePosts();

  if (!user) {
    return <LoadingSpinner fullScreen />;
  }

  const statsCards = [
    {
      title: 'My Clubs',
      value: user.clubs?.length || 0,
      icon: <Group />,
      color: 'primary.main',
      action: 'Browse Clubs',
    },
    {
      title: 'Campus Posts',
      value: posts?.length || 0,
      icon: <Feed />,
      color: 'success.main',
      action: 'View Feed',
    },
    {
      title: 'My Badges',
      value: user.badges?.length || 0,
      icon: <EmojiEvents />,
      color: 'warning.main',
      action: 'View Profile',
    },
    {
      title: isFaculty ? 'Pending Approvals' : 'My Applications',
      value: isFaculty ? 5 : 2, // Mock data
      icon: <Assignment />,
      color: 'info.main',
      action: isFaculty ? 'Review Students' : 'View Applications',
    },
  ];

  return (
    <Box>
      {/* Welcome Section */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <UserAvatar user={user} size="large" showRole />
          <Box sx={{ ml: 2 }}>
            <Typography variant="h4" component="h1" sx={{ fontWeight: 700, mb: 0.5 }}>
              Welcome back, {user.name?.split(' ')[0] || 'User'}!
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <RoleBadge role={user.role} />
              <Typography variant="body2" color="text.secondary">
                {user.branch}
              </Typography>
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Stats Grid - Using flexbox instead of Grid */}
      <Box sx={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: 3,
        mb: 4,
        '& > *': {
          flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 12px)', md: '1 1 calc(25% - 18px)' }
        }
      }}>
        {statsCards.map((stat, index) => (
          <Card
            key={index}
            sx={{
              minWidth: 200,
              transition: 'all 0.2s ease-in-out',
              '&:hover': {
                transform: 'translateY(-2px)',
                boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
              },
            }}
          >
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Avatar
                  sx={{
                    bgcolor: stat.color,
                    width: 48,
                    height: 48,
                    mr: 2,
                  }}
                >
                  {stat.icon}
                </Avatar>
                <Box>
                  <Typography variant="h4" component="div" sx={{ fontWeight: 700 }}>
                    {stat.value}
                  </Typography>
                  <Typography color="text.secondary" variant="body2">
                    {stat.title}
                  </Typography>
                </Box>
              </Box>
              <Button
                size="small"
                variant="text"
                sx={{ mt: 1 }}
                endIcon={<TrendingUp />}
              >
                {stat.action}
              </Button>
            </CardContent>
          </Card>
        ))}
      </Box>

      {/* Recent Activity - Using flexbox instead of Grid */}
      <Box sx={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: 3,
        '& > *': {
          flex: { xs: '1 1 100%', md: '1 1 calc(50% - 12px)' }
        }
      }}>
        {/* My Clubs */}
        <Card sx={{ minWidth: 300 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
              My Clubs
            </Typography>

            {clubsLoading ? (
              <LoadingSpinner message="Loading clubs..." />
            ) : clubsError ? (
              <ErrorMessage error={clubsError} />
            ) : (user.clubs && user.clubs.length > 0) ? (
              <Box>
                {user.clubs.slice(0, 3).map((club, index) => (
                  <Box
                    key={club.id || index}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      py: 1,
                      borderBottom: '1px solid',
                      borderColor: 'divider',
                      '&:last-child': {
                        borderBottom: 'none',
                      },
                    }}
                  >
                    <Avatar sx={{ mr: 2 }}>
                      {club.logo || '🏛️'}
                    </Avatar>
                    <Box sx={{ flexGrow: 1 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {club.name || 'Unknown Club'}
                      </Typography>
                      <Chip
                        label={club.memberRole || 'Member'}
                        size="small"
                        variant="outlined"
                        sx={{ mt: 0.5 }}
                      />
                    </Box>
                  </Box>
                ))}
                {user.clubs.length > 3 && (
                  <Button size="small" sx={{ mt: 1 }}>
                    View all {user.clubs.length} clubs
                  </Button>
                )}
              </Box>
            ) : (
              <Box sx={{ textAlign: 'center', py: 3 }}>
                <Typography color="text.secondary" gutterBottom>
                  You haven't joined any clubs yet
                </Typography>
                <Button variant="outlined" size="small">
                  Browse Clubs
                </Button>
              </Box>
            )}
          </CardContent>
        </Card>

        {/* Goals & Progress */}
        <Card sx={{ minWidth: 300 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
              My Goals
            </Typography>

            {user.goals ? (
              <Box>
                <Typography variant="body2" color="text.secondary" paragraph>
                  {user.goals}
                </Typography>
                <Box sx={{ mt: 2 }}>
                  <Typography variant="caption" color="text.secondary">
                    Progress towards your goals
                  </Typography>
                  <LinearProgress
                    variant="determinate"
                    value={65} // Mock progress
                    sx={{
                      mt: 1,
                      height: 8,
                      borderRadius: 4,
                    }}
                  />
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                    You're 65% of the way there!
                  </Typography>
                </Box>
              </Box>
            ) : (
              <Box sx={{ textAlign: 'center', py: 3 }}>
                <Typography color="text.secondary" gutterBottom>
                  Set your career goals to track progress
                </Typography>
                <Button variant="outlined" size="small">
                  Add Goals
                </Button>
              </Box>
            )}
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
};

export default DashboardPage;